import { createAction } from '@reduxjs/toolkit';

import { getPayloadCreator, type Meta } from '../core/actionHelpers';
import type { BaseResource, QcsSyncResourcesPayload } from '../core/resourceStoreTypes';
import type {
  DeleteSavedReportConfigurationPayload,
  OpenReportConfigurationPayload,
  RenameReportConfigurationPayload,
  ReportConfiguration,
  ReportConfigurationProps,
  SaveReportConfigurationPayload,
} from './reportConfigurationsTypes';
import type { AdjustedmentProps } from './utils/reportFieldAvailability';

export type REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD = ReportConfiguration;
export type REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD = ReportConfiguration[];
export type REPORT_CONFIGURATIONS_ERROR_PAYLOAD = unknown; // what the request threw, or its fields spread onto the resource's id

/** What create and update handlers read from `meta.props`. */
type ReportConfigurationMeta = Meta<
  BaseResource,
  unknown,
  { adjustReportConfiguration?: boolean; adjustProps?: AdjustedmentProps; generateReport?: boolean; location?: string }
>;
const reportConfigurationPayloadCreator = getPayloadCreator<
  REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD,
  BaseResource,
  ReportConfigurationMeta
>();

export const getReportConfigurations = createAction('GET_REPORT_CONFIGURATIONS', getPayloadCreator());

export const getReportConfigurationsSuccess = createAction(
  'GET_REPORT_CONFIGURATIONS_SUCCESS',
  getPayloadCreator<QcsSyncResourcesPayload<ReportConfigurationProps>>(),
);

export const getReportConfigurationsFailure = createAction(
  'GET_REPORT_CONFIGURATIONS_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const createReportConfiguration = createAction('CREATE_REPORT_CONFIGURATION', reportConfigurationPayloadCreator);

export const createReportConfigurationSuccess = createAction(
  'CREATE_REPORT_CONFIGURATION_SUCCESS',
  reportConfigurationPayloadCreator,
);

export const createReportConfigurationFailure = createAction(
  'CREATE_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const updateReportConfiguration = createAction('UPDATE_REPORT_CONFIGURATION', reportConfigurationPayloadCreator);

export const updateReportConfigurationSuccess = createAction(
  'UPDATE_REPORT_CONFIGURATION_SUCCESS',
  reportConfigurationPayloadCreator,
);

export const updateReportConfigurationFailure = createAction(
  'UPDATE_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const deleteReportConfiguration = createAction(
  'DELETE_REPORT_CONFIGURATION',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>(),
);

export const deleteReportConfigurationSuccess = createAction(
  'DELETE_REPORT_CONFIGURATION_SUCCESS',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>(),
);

export const deleteReportConfigurationFailure = createAction(
  'DELETE_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

// Internal action for adjusted update after processing
export const updateReportConfigurationInternal = createAction(
  'UPDATE_REPORT_CONFIGURATION_INTERNAL',
  reportConfigurationPayloadCreator,
);

// Open/Save/Delete-saved actions
export const openReportConfiguration = createAction(
  'OPEN_REPORT_CONFIGURATION',
  getPayloadCreator<OpenReportConfigurationPayload>(),
);
export const openReportConfigurationSuccess = createAction(
  'OPEN_REPORT_CONFIGURATION_SUCCESS',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>(),
);
export const openReportConfigurationFailure = createAction(
  'OPEN_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const saveReportConfiguration = createAction(
  'SAVE_REPORT_CONFIGURATION',
  getPayloadCreator<SaveReportConfigurationPayload>(),
);
export const saveReportConfigurationSuccess = createAction(
  'SAVE_REPORT_CONFIGURATION_SUCCESS',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>(),
);
export const saveReportConfigurationFailure = createAction(
  'SAVE_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const deleteSavedReportConfiguration = createAction(
  'DELETE_SAVED_REPORT_CONFIGURATION',
  getPayloadCreator<DeleteSavedReportConfigurationPayload>(),
);
export const deleteSavedReportConfigurationSuccess = createAction(
  'DELETE_SAVED_REPORT_CONFIGURATION_SUCCESS',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>(),
);
export const deleteSavedReportConfigurationFailure = createAction(
  'DELETE_SAVED_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);

export const renameReportConfiguration = createAction(
  'RENAME_REPORT_CONFIGURATION',
  getPayloadCreator<RenameReportConfigurationPayload>(),
);
export const renameReportConfigurationSuccess = createAction(
  'RENAME_REPORT_CONFIGURATION_SUCCESS',
  getPayloadCreator<REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>(),
);
export const renameReportConfigurationFailure = createAction(
  'RENAME_REPORT_CONFIGURATION_FAILURE',
  getPayloadCreator<REPORT_CONFIGURATIONS_ERROR_PAYLOAD>(),
);
